import {
  Component,
  computed,
  inject,
  signal,
  type Signal,
  type WritableSignal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";

import { ActingOrgService } from "../../../core/services/acting-org.service";
import { AuthStateService } from "../../../core/services/auth-state.service";
import { OrganizationsService } from "../../../core/services/organizations.service";
import { PortalDataService } from "../../../core/services/portal-data.service";

import type { IOrganization } from "../../../core/models/i-organization";

/**
 * The ⌘K organization switcher — artboard 5a.
 *
 * Rendered by the shell for a Davaco Admin only. It never decides authority:
 * choosing an organization sets the acting slug, which travels as
 * `X-Acting-Org`, and the API honours it only for a verified token carrying the
 * admin role.
 */
@Component({
  selector: "app-org-switcher",
  standalone: true,
  imports: [FormsModule],
  templateUrl: "./org-switcher.component.html",
  styleUrl: "./org-switcher.component.scss",
  host: {
    "(document:keydown)": "onKeydown($event)",
  },
})
export class OrgSwitcherComponent {

  private readonly organizations = inject(OrganizationsService);

  private readonly actingOrg = inject(ActingOrgService);

  private readonly authState = inject(AuthStateService);

  private readonly data = inject(PortalDataService);

  public readonly open: WritableSignal<boolean> = signal<boolean>(false);

  public readonly query: WritableSignal<string> = signal<string>("");

  public readonly highlighted: WritableSignal<number> = signal<number>(0);

  public readonly matches: Signal<IOrganization[]> = computed((): IOrganization[] => {
    const term: string = this.query()
.trim()
.toLowerCase();
    const all: IOrganization[] = this.organizations.organizations.value();

    if (term.length === 0) {
      return all;
    }

    // Name OR slug: an admin typing "sbux" is thinking of the hostname, one
    // typing "Starbucks" is reading the table. Both have to land.
    return all.filter(
      (organization: IOrganization): boolean =>
        organization.displayName.toLowerCase()
.includes(term) ||
        organization.slug.toLowerCase()
.includes(term)
    );
  });

  public onKeydown(event: KeyboardEvent): void {
    if (!this.authState.isDavacoAdmin()) {
      return;
    }

    const isOpenShortcut: boolean = (event.metaKey || event.ctrlKey) && event.key === "k";
    if (isOpenShortcut) {
      // Chrome binds Ctrl+K to the address bar, so this only works if the
      // default is refused.
      event.preventDefault();
      this.toggle();
      return;
    }

    if (!this.open()) {
      return;
    }

    if (event.key === "Escape") {
      this.close();
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step: number = event.key === "ArrowDown" ? 1 : -1;
      const count: number = this.matches().length;
      if (count > 0) {
        this.highlighted.update((index: number): number => (index + step + count) % count);
      }
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const chosen: IOrganization | undefined = this.matches()[this.highlighted()];
      if (chosen !== undefined) {
        this.viewAs(chosen);
      }
    }
  }

  public toggle(): void {
    this.open.update((open: boolean): boolean => !open);
    if (this.open()) {
      this.query.set("");
      this.highlighted.set(0);
    }
  }

  public close(): void {
    this.open.set(false);
  }

  public viewAs(organization: IOrganization): void {
    this.actingOrg.viewAs(organization.slug);
    // The resources key off the acting slug, but a reload here makes the switch
    // immediate rather than waiting for the next route change.
    this.data.reloadAll();
    this.close();
  }

  public onQueryChange(value: string): void {
    this.query.set(value);
    // Reset the cursor: leaving it on index 3 after the list shrinks to two
    // means Enter selects nothing and the overlay appears to hang.
    this.highlighted.set(0);
  }
}
